import React from 'react'
import { ContractDataDto, ContractCostItem } from '../../api/estimates/types/model'
import { Icon } from '../../shared/ui/Icon'
import {
  ExpandedContainer,
  DocumentBlock,
  DocumentHeader,
  DocumentTitle,
  StatusBadge,
  DocumentDescription,
  DocumentInfo,
  DocumentNumber,
  DocumentDate,
  DocumentAmount,
  DetailsLeft,
  DetailsRight,
  ProjectSummary,
  VatInfo,
  CostItemsList,
  CostItemBadge,
  ObjectBlock,
  ContractPairBlock,
  DetailRow
} from './ContractExpandedDetails.styles'

interface ContractExpandedDetailsProps {
  contract: ContractDataDto
}

const formatAmount = (amount: number): string => {
  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount) + ' ₽'
}

const formatDate = (dateString?: string): string | undefined => {
  if (!dateString) return undefined
  try {
    return new Date(dateString).toLocaleDateString('ru-RU')
  } catch {
    return undefined
  }
}

const calculateVat = (amount: number, vatRate: number = 20): number => {
  return (amount * vatRate) / (100 + vatRate)
}

const groupCostItemsByObject = (costItems: ContractCostItem[]): Map<string, ContractCostItem[]> => {
  const grouped = new Map<string, ContractCostItem[]>()
  
  costItems.forEach(item => {
    const objectId = item.objectGuid || 'default'
    if (!grouped.has(objectId)) {
      grouped.set(objectId, [])
    }
    grouped.get(objectId)!.push(item)
  })
  
  return grouped
}

export const ContractExpandedDetails: React.FC<ContractExpandedDetailsProps> = ({ contract }) => {
  return (
    <ExpandedContainer>
      {contract.details?.map((detail, index) => {
        const detailDate = formatDate(detail.date)
        
        return (
          <DetailRow key={index}>
            <DetailsLeft>
              <ContractPairBlock>
                {/* Блок контракта */}
                <DocumentBlock>
                  <DocumentHeader>
                    <DocumentTitle>
                      <Icon type="document" size={20} color="#3498db" />
                      {detail.isMain ? 'Основной договор' : 'Дополнительное соглашение'}
                    </DocumentTitle>
                    {detail.status && <StatusBadge status="concluded">{detail.status}</StatusBadge>}
                  </DocumentHeader>
                  <DocumentDescription>{detail.name || 'Договор'}</DocumentDescription>
                  <DocumentInfo>
                    {detail.number && (
                      <DocumentNumber>
                        {detail.number}
                        {detailDate && ' от'}
                      </DocumentNumber>
                    )}
                    {detailDate && <DocumentDate>{detailDate}</DocumentDate>}
                  </DocumentInfo>
                  <DocumentAmount color="#3498db">{formatAmount(detail.sum)}</DocumentAmount>
                </DocumentBlock>

                {/* Блок сметы (опционально) */}
                {detail.pdc && (
                  <DocumentBlock>
                    <DocumentHeader>
                      <DocumentTitle>
                        <Icon type="document" size={20} color="#2ecc71" />
                        Смета
                      </DocumentTitle>
                      { detail.pdc.status && <StatusBadge status="approved">{detail.pdc.status}</StatusBadge> }
                    </DocumentHeader>
                    <DocumentDescription>
                    </DocumentDescription>
                    <DocumentInfo>
                      <DocumentTitle>
                        {detail.pdc.name}
                      </DocumentTitle>
                      {detail.pdc.number && (
                        <DocumentNumber>
                          {detail.pdc.number}
                          {detail.pdc.date && ` от ${detail.pdc.date}`}
                        </DocumentNumber> 
                      )}
                    </DocumentInfo>
                    <DocumentAmount color="#2ecc71">{formatAmount(detail.pdc.sum)}</DocumentAmount>
                  </DocumentBlock>
                )}
              </ContractPairBlock>
            </DetailsLeft>

            <DetailsRight>
              {detail.objects && (
                <>
                  {detail.objects.map((object) => {
                    return (
                      <ObjectBlock key={`${object.name}-${object.date}`}>
                        <ProjectSummary>
                          {object.name}
                        </ProjectSummary>
                        {object.costItems.length > 0 && (
                          <CostItemsList>
                            {object.costItems.map((item) => {
                              const itemAmount = item.amountTotalRub || item.amount || 0
                              const itemName = item.costItemName || `Статья ${item.costItemGuid}`
                              const displayText = item.costItemIdpItemCode 
                                ? `${item.costItemIdpItemCode} ${itemName}: ${formatAmount(itemAmount)}`
                                : `${itemName}: ${formatAmount(itemAmount)}`
                              return (
                                <CostItemBadge key={item.guid}>
                                  {displayText}
                                </CostItemBadge>
                              )
                            })}
                          </CostItemsList>
                        )}
                      </ObjectBlock>
                    )
                  })}
                </>
              )}
            </DetailsRight>
          </DetailRow>
        )
      })}
    </ExpandedContainer>
  )
}


